"use client";
import React, { useState, useEffect } from "react";
import styles from "./CampaignAnalytics.module.css";
import CampaignSnapshot from "./CampaignSnapshot/CampaignSnapshot";
import PerformanceAnalytics from "./PerformanceAnalytics/PerformanceAnalytics";
import ActivePartnerships from "./ActivePartnerships/ActivePartnerships";
import CreatorDashboard from "../CreatorDashboard/CreatorDashboard";
import AdminPage from "../AdminPage/AdminPage";

const Sidebar = ({ mobileOpen, onClose, currentView, onViewChange }) => {
  return (
    <aside
      className={
        styles.sidebar + (mobileOpen ? " " + styles.sidebarMobileOpen : "")
      }
    >
      <nav className={styles.navigation}>
        <h1 className={styles.sidebarTitle}>Campaign Management</h1>
        <ul className={styles.menu}>
          <li
            className={
              currentView === "analytics"
                ? styles.menuItemActive
                : styles.menuItem
            }
            onClick={() => onViewChange("analytics")}
            style={{ cursor: "pointer" }}
          >
            <img
              src="https://cdn.builder.io/api/v1/image/assets/TEMP/2e5c24e8b235fcbbb292bdbf7256eac019b358fa?placeholderIfAbsent=true&apiKey=dc2f6a1e66164bfb9d194f96d43a57dd"
              alt=""
              className={styles.menuIcon}
            />
            <span>Campaign Analytics</span>
          </li>
          <li
            className={
              currentView === "creator"
                ? styles.menuItemActive
                : styles.menuItem
            }
            onClick={() => onViewChange("creator")}
            style={{ cursor: "pointer" }}
          >
            <img
              src="https://cdn.builder.io/api/v1/image/assets/TEMP/4327f35424ea6a0e92e1f87904bf1b108d42dcfb?placeholderIfAbsent=true&apiKey=dc2f6a1e66164bfb9d194f96d43a57dd"
              alt=""
              className={styles.menuIcon}
            />
            <span>Creator Dashboard</span>
          </li>
          <li
            className={
              currentView === "admin"
                ? styles.menuItemActive
                : styles.menuItem
            }
            onClick={() => onViewChange("admin")}
            style={{ cursor: "pointer" }}
          >
            <img
              src="https://cdn.builder.io/api/v1/image/assets/TEMP/2e5c24e8b235fcbbb292bdbf7256eac019b358fa?placeholderIfAbsent=true&apiKey=dc2f6a1e66164bfb9d194f96d43a57dd"
              alt=""
              className={styles.menuIcon}
            />
            <span>Admin Page</span>
          </li>
        </ul>
      </nav>
    </aside>
  );
};

function getMinutesAgo(updatedAt) {
  if (!updatedAt) return null;
  const updatedDate = new Date(updatedAt);
  const now = new Date();
  const diffMs = now - updatedDate;
  const diffMins = Math.floor(diffMs / 60000);
  return diffMins;
}

const CampaignHeader = ({ campaignName, updatedAt }) => {
  const minsAgo = updatedAt ? getMinutesAgo(updatedAt) : null;
  return (
    <header className={styles.header}>
      <h1 className={styles.headerTitle}>{campaignName}</h1>
      <p className={styles.lastUpdate}>
        {minsAgo !== null
          ? `Last Update ${minsAgo} min${minsAgo === 1 ? "" : "s"} ago`
          : "Campaign has not started yet"}
      </p>
    </header>
  );
};

const CampaignAnalytics = ({
  campaignName,
  campaignId,
  updated_at,
  budget,
  target_views,
  ...rest
}) => {
  const [currentViews, setCurrentViews] = useState(null);
  const [latestViews, setLatestViews] = useState(null);
  const [percentChange, setPercentChange] = useState(null);
  const [latestLikes, setLatestLikes] = useState(null);
  const [latestComments, setLatestComments] = useState(null);
  const [likesCommentsPercentChange, setLikesCommentsPercentChange] =
    useState(null);
  const [engagementRate, setEngagementRate] = useState(null);

  const [audience, setAudience] = useState(null);
  const [latestRecordTime, setLatestRecordTime] = useState(null);
  const [currentView, setCurrentView] = useState("analytics"); // 'analytics', 'creator', or 'admin'

  const isMobile = typeof window !== "undefined" && window.innerWidth <= 991;

  const handleViewChange = (view) => {
    setCurrentView(view);
  };

  useEffect(() => {
    setCurrentViews(null);
    setLatestViews(null);
    setPercentChange(null);
    setLatestLikes(null);
    setLatestComments(null);
    setLikesCommentsPercentChange(null);
    setEngagementRate(null);
    setAudience(null);
  }, [campaignId]);

  useEffect(() => {
    if (!campaignId) return;
    const fetchAudience = async () => {
      try {
        const axios = (await import("axios")).default;
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/campaign-extras?campaign_id=${campaignId}`,
        );
        
        // 打印campaign-extras的返回信息
        console.log('Campaign-extras API返回:', res.data);
        
        // 灵活获取audience数据，支持不同的key名称
        const extrasJson = res.data?.extras_json || {};
        const audienceNum = extrasJson.audience || extrasJson.Audience;
        setAudience(audienceNum);
      } catch (e) {
        setAudience(null);
      }
    };
    fetchAudience();
  }, [campaignId]);

  return (
    <main className={styles.container}>
      <div className={styles.layout}>
        {!isMobile && (
          <Sidebar
            mobileOpen={false}
            onClose={() => {}}
            currentView={currentView}
            onViewChange={handleViewChange}
          />
        )}
        <section className={styles.mainContent}>
          <CampaignHeader
            campaignName={campaignName}
            updatedAt={latestRecordTime}
          />

          {currentView === "analytics" ? (
            <>
              <CampaignSnapshot
                budget={budget}
                targetViews={target_views}
                currentViews={currentViews}
                latestViews={latestViews}
                percentChange={percentChange}
                latestLikes={latestLikes}
                latestComments={latestComments}
                likesCommentsPercentChange={likesCommentsPercentChange}
                engagementRate={engagementRate}
                audience={audience}
              />
              <PerformanceAnalytics
                campaignId={campaignId}
                targetViews={target_views}
                setCurrentViews={setCurrentViews}
                setLatestViews={setLatestViews}
                setPercentChange={setPercentChange}
                setLatestLikes={setLatestLikes}
                setLatestComments={setLatestComments}
                setLikesCommentsPercentChange={setLikesCommentsPercentChange}
                setEngagementRate={setEngagementRate}
                setLatestRecordTime={setLatestRecordTime}
              />
              <ActivePartnerships campaignId={campaignId} />
            </>
          ) : currentView === "creator" ? (
            <CreatorDashboard />
          ) : (
            <AdminPage />
          )}
        </section>
      </div>
    </main>
  );
};

export default CampaignAnalytics;
